import { Client, Account, Databases, Storage, Functions, Query, ID, Permission, Role } from 'appwrite';

// Initialize client
const client = new Client();

// Configure client
// Self-hosted Appwrite. Never use api.jadartx.com — that host's TLS is down.
const DEFAULT_ENDPOINT = 'https://api.jadartx.co.tz/v1';
const rawEndpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || DEFAULT_ENDPOINT;
const endpoint = String(rawEndpoint).includes('api.jadartx.com')
  ? DEFAULT_ENDPOINT
  : rawEndpoint;
const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID || '690f2df30013d4b7de87';

// Configure client with error handling
try {
  client
    .setEndpoint(endpoint)
    .setProject(projectId);
} catch (error) {
  if (process.env.NODE_ENV !== 'production') {
    console.error('Error configuring Appwrite client:', error);
  }
}

// Initialize services
const account = new Account(client);
const databases = new Databases(client);
const storage = new Storage(client);
const functions = new Functions(client);

// VAT attachments bucket (create in Appwrite Console if missing)
export const VAT_ATTACHMENTS_BUCKET_ID = 'vat-attachments';

/** Product catalog images (create bucket in Appwrite; override via REACT_APP_PRODUCT_CATALOG_BUCKET_ID). */
export const PRODUCT_CATALOG_BUCKET_ID =
  process.env.REACT_APP_PRODUCT_CATALOG_BUCKET_ID || 'product-catalog';

/** TRA payment proof uploads (PDF/images). Run `npm run setup:tra-payment` or create in Console. */
export const TRA_PAYMENT_ATTACHMENTS_BUCKET_ID =
  process.env.REACT_APP_TRA_PAYMENT_BUCKET_ID || 'tra-payment-attachments';

/** Returns the public URL to view a file in storage (for use in links). */
export function getStorageFileViewUrl(fileId) {
  return getStorageFileViewUrlForBucket(VAT_ATTACHMENTS_BUCKET_ID, fileId);
}

/**
 * View URL for an arbitrary bucket/file (e.g. catalog images, company docs).
 * `REACT_APP_APPWRITE_ENDPOINT` must include the API version, e.g. https://host/v1
 * (same as Client.setEndpoint). Path is /v1/storage/buckets/... not /v1/storage/v1/...
 */
export function getStorageFileViewUrlForBucket(bucketId, fileId) {
  if (!fileId || !bucketId) return '';
  const base = (process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://api.jadartx.co.tz/v1').replace(/\/$/, '');
  const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID || '690f2df30013d4b7de87';
  return `${base}/storage/buckets/${bucketId}/files/${fileId}/view?project=${encodeURIComponent(projectId)}`;
}

/**
 * Download a storage file using the same auth as the Appwrite SDK (e.g. X-Fallback-Cookies).
 * Prefer this over a bare /view URL in <img src> when the bucket is not publicly readable.
 * @param {string} bucketId
 * @param {string} fileId
 * @returns {Promise<Blob>}
 */
export async function getStorageFileBlobForBucket(bucketId, fileId) {
  if (!fileId || !bucketId) throw new Error('Missing file or bucket');
  const downloadUrl = storage.getFileDownload({ bucketId, fileId });
  const urlObj = new URL(downloadUrl);
  const { uri, options } = storage.client.prepareRequest('GET', urlObj, {}, {});
  const res = await fetch(uri, options);
  if (!res.ok) {
    const snippet = (await res.text().catch(() => '')).slice(0, 200);
    throw new Error(snippet || `Failed to load file (${res.status})`);
  }
  return res.blob();
}

/**
 * Fetches a file from the VAT attachments bucket using the current session (works with private buckets).
 * @param {string} fileId - Storage file ID
 * @returns {Promise<Blob>}
 */
export async function getStorageFileBlob(fileId) {
  return getStorageFileBlobForBucket(VAT_ATTACHMENTS_BUCKET_ID, fileId);
}

// Export services
export { account, databases, storage, functions, Query, ID, Permission, Role, endpoint, projectId };
export default client;
